// Copyright (c) Meta Platforms, Inc. and affiliates.

'use client';

/**
 * @file NavHeadingMenuItem.tsx
 * @input Uses React, StyleX, Icon, Text, NavMenuContext, useLinkComponent
 * @output Exports NavHeadingMenuItem component and NavHeadingMenuItemProps type
 * @position Core implementation; consumed by index.ts
 *
 * SYNC: When modified, update:
 * - /packages/core/src/NavMenu/index.ts
 */
import React, { useCallback } from 'react';
import * as stylex from '@stylexjs/stylex';
import { renderIconSlot } from "../Icon/index.js";
import { Text } from "../Text/index.js";
import "../theme/tokens.stylex.js";
import { colorVars, spacingVars, typographyVars, typeScaleVars, radiusVars } from "../theme/tokens.stylex.js";
import { mergeProps } from "../utils/index.js";
import { useNavHeadingMenuContext } from "./NavMenuContext.js";
import { useLinkComponent } from "../Link/useLinkComponent.js";
import { themeProps } from "../utils/themeProps.js";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const styles = {
  root: {
    kB7OPa: "x9f619",
    k1xSpc: "x78zum5",
    kGNEyG: "x6s0dn4",
    kOIVth: "x1txdalj",
    kzqmXN: "xh8yej3",
    kaIpWk: "xh6dtrn",
    kMv6JI: "x9ynric",
    kGuDYH: "xcr08ib",
    kMwMTN: "x1tgivj0",
    kWkggS: "xjbqb8w x1c52tdz xe9uy6x",
    kkrTdU: "x1ypdohk",
    k9WMMc: "xdpxx8g",
    kI3sdo: "x1a2a7pz",
    kybGjl: "x1hl2dhg",
    $$css: true
  },
  disabled: {
    kSiTet: "xbyyjgo",
    kkrTdU: "x1h6gzvc",
    $$css: true
  }
};
const sizeStyles = {
  sm: {
    k8WAf4: "xu0wf1k",
    kg3NbH: "xf314gf",
    $$css: true
  },
  md: {
    k8WAf4: "xce4md1",
    kg3NbH: "xf314gf",
    $$css: true
  },
  lg: {
    k8WAf4: "x8o8v82",
    kg3NbH: "xrrkdod",
    $$css: true
  }
};
/**
 * Menu item for nav heading popovers.
 *
 * Reads size from the parent NavHeadingMenu for consistent padding.
 * Automatically dismisses the menu on click via context.
 * Renders as a link when `href` is provided.
 *
 * @example
 * ```
 * <NavHeadingMenu>
 *   <NavHeadingMenuItem label="Dashboard" href="/dashboard" />
 *   <NavHeadingMenuItem label="Settings" icon={GearIcon} onClick={open} />
 * </NavHeadingMenu>
 * ```
 */
export function NavHeadingMenuItem({
  ref,
  icon,
  label,
  description,
  href,
  onClick,
  isDisabled = false,
  xstyle,
  className,
  style,
  'data-testid': testId
}) {
  const ctx = useNavHeadingMenuContext();
  const size = ctx?.size ?? 'md';
  const handleClick = useCallback(() => {
    if (isDisabled) {
      return;
    }
    onClick?.();
    ctx?.closeMenu();
  }, [isDisabled, onClick, ctx]);
  const LinkComponent = useLinkComponent();
  const Element = href ? LinkComponent : 'div';
  return /*#__PURE__*/_jsxs(Element, {
    ref: ref,
    role: "menuitem",
    tabIndex: isDisabled ? undefined : -1,
    "aria-disabled": isDisabled || undefined,
    href: href,
    onClick: handleClick,
    "data-testid": testId,
    ...mergeProps(themeProps('nav-heading-menu-item', {
      size
    }), stylex.props(styles.root, sizeStyles[size], isDisabled && styles.disabled, xstyle), className, style),
    children: [icon && renderIconSlot(icon, {
      size: 'sm',
      color: 'secondary'
    }), /*#__PURE__*/_jsxs("span", {
      ...{
        className: "x78zum5 xdt5ytf x98rzlu xeuugli"
      },
      children: [typeof label === 'string' ? /*#__PURE__*/_jsx(Text, {
        type: "body",
        maxLines: 1,
        children: label
      }) : label, description && /*#__PURE__*/_jsx(Text, {
        type: "supporting",
        maxLines: 1,
        children: description
      })]
    })]
  });
}
NavHeadingMenuItem.displayName = 'NavHeadingMenuItem';